// moms-harness:anchor v2
// Spec: docs/spec/auth/SPEC-auth-legacy-password-field-cleanup-A.yaml

import { LoginRes, LoginUserInfo, UserInfoOnSession } from '@/services/auth/login.types'

export type AuthMode = 'okta' | 'knox'

// LOGISPRD-10673: 세션에서 제거된 레거시 비밀번호 필드. 백엔드가 계속 내려보낼 수 있으므로
// 세션에 기록하기 직전에 걸러낸다.
const LEGACY_PASSWORD_FIELDS = ['modifyFlag', 'pw_valid_date', 'temporaryPassword']

const omitLegacyPasswordFields = <T extends object>(source: T): T => {
  const sanitized = { ...source } as Record<string, unknown>
  LEGACY_PASSWORD_FIELDS.forEach((field) => delete sanitized[field])

  // deptInfo(부서장에게만 내려오는 팀원 목록)는 항목마다 pw_valid_date 를 싣고 온다.
  // 최상위만 지우면 남의 비밀번호 유효기간이 세션에 그대로 남으므로 항목까지 같은 기준으로 지운다.
  if (Array.isArray(sanitized.deptInfo)) {
    sanitized.deptInfo = sanitized.deptInfo.map((member) =>
      member !== null && typeof member === 'object' ? omitLegacyPasswordFields(member as object) : member,
    )
  }

  return sanitized as T
}

export class AuthStorage {
  static auth = {
    name: 'authToken',
    get() {
      return sessionStorage.getItem(this.name) || ''
    },
    set(token: string) {
      sessionStorage.setItem(this.name, token || '')
    },
    clear() {
      sessionStorage.removeItem(this.name)
    },
  }

  // LOGISPRD-7099: 현재 세션의 로그인 방식. 토큰 선택/로그아웃 분기 기준(잔존 Okta 토큰 오염 방지).
  static authMode = {
    name: 'authMode',
    get(): AuthMode | '' {
      return (sessionStorage.getItem(this.name) as AuthMode) || ''
    },
    set(mode: AuthMode) {
      sessionStorage.setItem(this.name, mode)
    },
    clear() {
      sessionStorage.removeItem(this.name)
    },
  }

  // LOGISPRD-9092: Okta access JWT 의 sessionStorage 평문 미러. 1.0 iframe(okta-auth-js SDK 없음)이
  // same-origin 으로 읽어 Authorization: Bearer 를 조립(LOGISPRD-8774)한다. set/clear 는 oktaAuth.ts 의
  // tokenManager 이벤트 구독(subscribeOktaTokenMirror)으로 일원화. 저장 포맷은 raw JWT(Bearer 접두 없음).
  // LLD anchor: docs/lld/auth/LLD-9092-okta-token-mirror.md "## 외부 경계 인터페이스 (1)", "## 데이터 모델".
  static accessToken = {
    name: 'oktaAccessToken',
    // null/부재 → '' 로 정규화. "빈 문자열" == "키 부재" == "유효 토큰 없음".
    get(): string {
      return sessionStorage.getItem(this.name) || ''
    },
    // best-effort: QuotaExceeded / 스토리지 차단 등에도 2.0 동작에 영향 주지 않도록 swallow.
    set(token: string) {
      try {
        sessionStorage.setItem(this.name, token || '')
      } catch {
        // best-effort + fail-safe: 쓰기 실패 시 stale 미러(fail-open) 를 남기지 않고 제거 —
        // 1.0 은 빈값 → 401 → 재인증 위임. 스토리지 완전 차단 시엔 removeItem 도 swallow.
        try {
          sessionStorage.removeItem(this.name)
        } catch {
          // best-effort
        }
      }
    },
    clear() {
      sessionStorage.removeItem(this.name)
    },
  }

  static user = {
    name: 'userInfo',
    get() {
      let userInfo: Partial<UserInfoOnSession> = {}

      try {
        const curUserInfo = sessionStorage.getItem(this.name) || ''
        if (curUserInfo) {
          const info = JSON.parse(curUserInfo)
          userInfo = {
            ...info,
            shipperName: info.shipper_name,
            shipperCode: info.shipper_code,
          }
        }
      } catch (e) {
        // ERROR
        // console.dir(e)
      }

      return userInfo
    },
    // LOGISPRD-10673: 세션 기록의 단일 진입점. 레거시 비밀번호 필드 제거는 여기 한 곳에서만 수행한다 —
    // setBy* 뿐 아니라 직접 호출자(useAuthQueries, MainHeader)까지 같은 경계를 지난다.
    set(userInfo: Partial<UserInfoOnSession>) {
      sessionStorage.setItem(this.name, JSON.stringify(omitLegacyPasswordFields(userInfo)))
    },
    setByLogin({ token, clientList, user }: Partial<LoginRes>) {
      if (user) {
        AuthStorage.user.set({
          ...user,
          clientList,
          // FIXME 1.0 용 (나중에 제거)
          id: user.email,
          username: user.nickname,
          token,
          auth: user.AUTH_CD,
          company: user.SPR_NM,
          deptname: user.DEPT_NM,
          orgComNm: user.ORG_SPR_NM,
          isConvertible: user.is_convertible,
        } as Partial<UserInfoOnSession>)
      }
    },
    setByAuthChecker(user: LoginUserInfo) {
      if (user) {
        AuthStorage.user.set({
          ...AuthStorage.user.get(),
          ...user,
          // 1.0 호환 파생 필드 (setByLogin 과 동일) — Okta 세션에서도 채워야
          // 메뉴 요청 SPR_NM(=company), 화주전환 auth 등이 정상 동작한다.
          auth: user.AUTH_CD,
          company: user.SPR_NM,
          deptname: user.DEPT_NM,
          orgComNm: user.ORG_SPR_NM,
          id: user.email,
          username: user.nickname,
          isConvertible: user.is_convertible,
        } as Partial<UserInfoOnSession>)
      }
    },
    clear() {
      sessionStorage.removeItem(this.name)
    },
  }
}
